import { StaticDataObject } from "context/staticData/staticData";
import { AnyObject } from "global";
import { Column, TableState } from "react-table";
import CellEllipsis from "shared/core/Table/components/cellEllipsis";
import { ProgramParticipantTag } from "shared/enums/ProgramParticipantTag";
import { Copies } from "shared/hooks/useCopies";
import { ApiProgramWorkFlowParticipantListing, SearchCriteriaItem } from "./types";
import BuyersProgramStatus from "shared/enums/BuyersProgramStatus";
import InPersonMeetupProgramStatus from "shared/enums/InPersonMeetupProgramStatus";
import { BuyerProgramsList, ProgramType } from "shared/enums/ProgramType";
import MeetupProgramStatus from "shared/enums/MeetupProgramStatus";
import ParticipantSubRole, { AddOrRemoveMeetingRepTagByDefault } from "shared/enums/ParticipantSubRole";
import { SearchCriteriaFieldName } from "./enum";
import intersection from "lodash/intersection";
import TabletalkProgramStatus from "shared/enums/TabletalkProgramStatus";
import TradeProgramStatus from "shared/enums/TradeProgramStatus";
import ConnectionsProgramStatus from "../../../../../../../shared/enums/ConnectionsProgramStatus";
import ColumnHeaderWithTimezone from "shared/core/Table/components/columnHeaderWithTimezone";
import CellDate from "shared/core/Table/components/cellDate";

export function isProgramInConfigurationStage(currentStage?: string, programType?: ProgramType) {
  if (!currentStage || !programType) return false;
  switch (programType) {
    case ProgramType.BUYERS_PROGRAM:
      return BuyersProgramStatus.CONFIGURATION === currentStage;
    case ProgramType.IN_PERSON_MEETUP_PROGRAM:
      return InPersonMeetupProgramStatus.CONFIGURATION === currentStage;
    case ProgramType.MEETUP_PROGRAM:
      return MeetupProgramStatus.CONFIGURATION === currentStage;
    case ProgramType.TRADE_PROGRAM:
      return TradeProgramStatus.CONFIGURATION === currentStage;
    case ProgramType.CONNECTIONS_PROGRAM:
      return ConnectionsProgramStatus.CONFIGURATION === currentStage;
    case ProgramType.TABLETALK_PROGRAM:
      return TabletalkProgramStatus.CONFIGURATION === currentStage;
    default:
      return false;
  }
}

export const fixedColumns = ["fullName"];

export const initialTableState: Partial<TableState<AnyObject>> = {
  pageIndex: 1,
  hiddenColumns: ["jobTitle", "prefix", "registrationDate"]
};

export function getTableColumns({
  copies,
  staticData,
  timeZoneDisplayStr
}: {
  copies: Copies;
  staticData: StaticDataObject;
  timeZoneDisplayStr: string;
}): Array<Column<ApiProgramWorkFlowParticipantListing>> {
  return [
    {
      id: "prefix",
      Header: copies.get("prefix"),
      accessor: d => staticData.prefix[d.prefix],
      width: 80,
      sortType: "basic",
      disableSortBy: true,
      Cell: CellEllipsis
    },
    {
      id: "fullName",
      Header: copies.get("name"),
      accessor: d => `${d.firstName} ${d.lastName}`,
      width: 120,
      sortType: "basic",
      disableSortBy: true,
      Cell: CellEllipsis
    },
    {
      id: "jobTitle",
      Header: copies.get("job.title"),
      accessor: "jobTitle",
      width: 100,
      sortType: "basic",
      disableSortBy: true,
      Cell: CellEllipsis
    },
    {
      id: "organizationName",
      Header: copies.get("organization.name"),
      accessor: "organizationName",
      width: 100,
      sortType: "basic",
      disableSortBy: true,
      Cell: CellEllipsis
    },
    {
      id: "workEmail",
      Header: copies.get("work.email"),
      accessor: "workEmail",
      width: 100,
      sortType: "basic",
      disableSortBy: true,
      Cell: CellEllipsis
    },
    {
      id: "confirmationNumber",
      Header: copies.get("confirmation.number"),
      accessor: "confirmationNumber",
      width: 100,
      sortType: "basic",
      disableSortBy: true,
      Cell: CellEllipsis
    },
    {
      id: "registrationDate",
      Header: <ColumnHeaderWithTimezone label={copies.get("registration.date")} timeZone={timeZoneDisplayStr} />,
      accessor: "registrationDate",
      width: 100,
      disableSortBy: true,
      Cell: CellDate
    },
    {
      id: "participantTypeDisplayLabel",
      Header: copies.get("participant.type"),
      accessor: "participantTypeDisplayLabel",
      width: 100,
      disableFilters: true,
      sortType: "basic",
      disableSortBy: true,
      Cell: CellEllipsis
    },
    {
      id: "subRole",
      Header: copies.get("participant.subrole"),
      accessor: d => d.attendeeParticipantSubRole && staticData.participantSubRole[d.attendeeParticipantSubRole],
      width: 100,
      disableFilters: true,
      sortType: "basic",
      disableSortBy: true,
      Cell: CellEllipsis
    },
    {
      id: "enrolledInProgram",
      Header: copies.get("status"),
      accessor: d => getEnrollmentStatusCopy(copies, d, staticData),
      width: 150,
      disableFilters: true,
      disableSortBy: true,
      Cell: CellEllipsis
    }
  ];
}

function getEnrollmentStatusCopy(
  copies: Copies,
  details: ApiProgramWorkFlowParticipantListing,
  staticData: StaticDataObject
) {
  if (details.enrolled) {
    if (details.tags?.length) {
      if (intersection([ProgramParticipantTag.PURE_BUYER, ProgramParticipantTag.SOCIAL_BUYER], details.tags).length) {
        return copies.get("enrolled.as", { 0: copies.get("buyer") });
      }
      return copies.get("enrolled.as", {
        0: details.tags.map((o: ProgramParticipantTag) => staticData.programParticipantTag[o]).join(", ")
      });
    }
    return copies.get("enrolled");
  }
  return copies.get("not.enrolled");
}

export function isBuyersProgramAttendeeOrSellerSubRole({
  programType,
  subRole
}: {
  programType?: ProgramType;
  subRole: ParticipantSubRole;
}) {
  return BuyerProgramsList.includes(programType as ProgramType) && AddOrRemoveMeetingRepTagByDefault.includes(subRole);
}

export function getSearchCriteriaIndividualUnits(items: Array<SearchCriteriaItem>) {
  return items.map((o: SearchCriteriaItem) => {
    if (
      [
        SearchCriteriaFieldName.PARTICIPANT_SUB_ROLE,
        SearchCriteriaFieldName.PARTICIPANT_TAG,
        SearchCriteriaFieldName.PARTICIPANT_TYPE
      ].includes(o.filterTypeId as SearchCriteriaFieldName)
    ) {
      // single select type fields so match type equal to
      return { key: o.filterTypeId, value: o.filterValueId, matchType: "EQUALS" };
    } else if (
      [SearchCriteriaFieldName.PARTICIPANT_NAME, SearchCriteriaFieldName.WORK_EMAIL].includes(
        o.filterTypeId as SearchCriteriaFieldName
      )
    ) {
      // encrypted fields and match type like
      return { key: o.filterTypeId, value: o.filterValueId, matchType: "LIKE", isEncrypted: true };
    }
    // general item with match type like
    return { key: o.filterTypeId, value: o.filterValueId, matchType: "LIKE" };
  });
}
